import AuthPageGuard from 'components/AuthPageGuard';
import { Box, Button, Card, Flex, Heading, Input, Text, VStack } from 'components/ui';
import { useTranslation } from 'hooks/useTranslation';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from 'services/authService';
import { getThemeColor } from 'theme/colors';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.forgotPassword(email);
      setSent(true);
    } catch {
      setError(t('app.auth.forgotPassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageGuard>
      <Box w="full">
        <VStack gap={8} align="stretch">
          <VStack gap={2} align="start">
            <Heading size="2xl" color={getThemeColor('text.primary')} fontWeight="bold">
              {t('app.auth.forgotPassword.title')}
            </Heading>
            <Text fontSize="md" color={getThemeColor('text.secondary')}>
              {t('app.auth.forgotPassword.subtitle')}
            </Text>
          </VStack>

          {sent ? (
            <Box
              p={4}
              borderRadius="lg"
              bg={getThemeColor('alert.success.bg')}
              border="1px solid"
              borderColor={getThemeColor('alert.success.border')}
              color={getThemeColor('alert.success.text')}
            >
              {t('app.auth.forgotPassword.successMessage')}
            </Box>
          ) : (
            <Card.Root>
              <Card.Body p={8}>
                <form onSubmit={handleSubmit}>
                  <VStack gap={5} align="stretch">
                    {error && (
                      <Box
                        p={3}
                        borderRadius="md"
                        bg={getThemeColor('alert.error.bg')}
                        border="1px solid"
                        borderColor={getThemeColor('alert.error.border')}
                        color={getThemeColor('alert.error.text')}
                      >
                        <Text fontSize="sm" fontWeight="medium">{error}</Text>
                      </Box>
                    )}

                    <Input
                      type="email"
                      label={t('app.auth.forgotPassword.emailLabel')}
                      placeholder={t('app.auth.forgotPassword.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      size="lg"
                      required
                    />

                    <Button
                      type="submit"
                      disabled={loading}
                      variant="solid"
                      colorPalette="blue"
                      size="lg"
                      w="full"
                      mt={2}
                    >
                      {loading ? t('app.auth.forgotPassword.sending') : t('app.auth.forgotPassword.submitButton')}
                    </Button>
                  </VStack>
                </form>
              </Card.Body>
            </Card.Root>
          )}

          <Flex justify="center">
            <Link to="/auth/sign-in">
              <Text fontSize="sm" fontWeight="semibold" color={getThemeColor('text.link')}>
                {t('app.auth.forgotPassword.backToSignIn')}
              </Text>
            </Link>
          </Flex>
        </VStack>
      </Box>
    </AuthPageGuard>
  );
}
